import { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  InputBase,
  Typography,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  useMediaQuery,
  Badge,
} from "@mui/material";
import {
  Search,
  Message,
  DarkMode,
  LightMode,
  Notifications,
  Help,
  Menu,
  Close,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setMode, setLogout } from "../../state";
import { useNavigate } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import ChatBox from "../chat/ChatBox";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user);
  const token = useSelector((state) => state.token);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isMobileMenuToggled, setIsMobileMenuToggled] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const isNonMobileScreens = useMediaQuery("(min-width: 1000px)");

  const theme = useTheme();
  const neutralLight = theme.palette.neutral.light;
  const dark = theme.palette.neutral.dark;
  const background = theme.palette.background.default;
  const primaryLight = theme.palette.primary.light;
  const alt = theme.palette.background.alt;

  const fullName = `${user?.firstName} ${user?.lastName}`;

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!searchQuery) {
        setSearchResults([]);
        return;
      }
      try {
        const response = await fetch(`https://dropie-service.onrender.com/users/search/all?query=${searchQuery}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        setSearchResults(data);
      } catch (err) {
        console.error(err);
      }
    };
    
    const delayDebounceFn = setTimeout(() => {
      fetchSearchResults();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, token]);

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`https://dropie-service.onrender.com/users/${user._id}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleClearNotifications = async () => {
    try {
      await fetch(`https://dropie-service.onrender.com/users/${user._id}/notifications`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications([]);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FlexBetween padding="1rem 6%" backgroundColor={alt}>
      <FlexBetween gap="1.75rem">
        <Typography
          fontWeight="bold"
          fontSize="clamp(1rem, 2rem, 2.25rem)"
          color="primary"
          onClick={() => navigate("/home")}
          sx={{
            "&:hover": {
              color: primaryLight,
              cursor: "pointer",
            },
          }}
        >
          Dropie
        </Typography>
        {isNonMobileScreens && (
          <Box position="relative">
            <FlexBetween
              backgroundColor={neutralLight}
              borderRadius="9px"
              gap="3rem"
              padding="0.1rem 1.5rem"
            >
              <InputBase 
                placeholder="Search..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <IconButton>
                <Search />
              </IconButton>
            </FlexBetween>
            {searchResults.length > 0 && (
              <Box
                position="absolute"
                top="100%"
                left="0"
                width="100%"
                backgroundColor={alt}
                borderRadius="0.5rem"
                mt="0.5rem"
                boxShadow="0 4px 6px rgba(0,0,0,0.1)"
                zIndex="10"
                maxHeight="300px"
                overflow="auto"
              >
                {searchResults.map((result) => (
                  <Box
                    key={result._id}
                    display="flex"
                    alignItems="center"
                    gap="1rem"
                    p="1rem"
                    sx={{
                      "&:hover": { backgroundColor: neutralLight, cursor: "pointer" }
                    }}
                    onClick={() => {
                      if (result.type === "user") {
                        navigate(`/profile/${result._id}`);
                      } else {
                        navigate(`/profile/${result.userId}`);
                      }
                      setSearchQuery("");
                      setSearchResults([]);
                    }}
                  >
                    {result.type === "user" ? (
                      <>
                        {result.picturePath ? (
                          <img style={{ objectFit: "cover", borderRadius: "50%" }} width="30px" height="30px" src={`https://dropie-service.onrender.com/assets/${result.picturePath}`} alt="user" />
                        ) : (
                          <Box width="30px" height="30px" borderRadius="50%" backgroundColor={dark} />
                        )}
                        <Typography color={dark}>{result.firstName} {result.lastName}</Typography>
                      </>
                    ) : (
                      <Box>
                        <Typography color={dark} fontWeight="bold">Post</Typography>
                        <Typography color={dark} fontSize="0.8rem">
                          {result.description.length > 30 ? result.description.substring(0, 30) + '...' : result.description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </FlexBetween>

      {/* DESKTOP NAV */}
      {isNonMobileScreens ? (
        <FlexBetween gap="2rem">
          <IconButton onClick={() => dispatch(setMode())}>
            {theme.palette.mode === "dark" ? (
              <DarkMode sx={{ fontSize: "25px" }} />
            ) : (
              <LightMode sx={{ color: dark, fontSize: "25px" }} />
            )}
          </IconButton>
          <IconButton onClick={() => setIsChatOpen(!isChatOpen)}>
            <Message sx={{ fontSize: "25px", color: dark }} />
          </IconButton>
          <Box position="relative">
            <IconButton onClick={() => { 
                setIsNotificationsOpen(!isNotificationsOpen);
                if (!isNotificationsOpen) fetchNotifications();
              }}>
              <Badge badgeContent={notifications.length} color="error">
                <Notifications sx={{ fontSize: "25px", color: dark }} />
              </Badge>
            </IconButton>
            {isNotificationsOpen && (
              <Box position="absolute" top="100%" right="-50px" width="300px" backgroundColor={alt} borderRadius="0.5rem" mt="0.5rem" boxShadow="0 4px 6px rgba(0,0,0,0.1)" zIndex="10" maxHeight="300px" overflow="auto" p="1rem">
                <FlexBetween mb="1rem">
                  <Typography fontWeight="bold">Notifications</Typography>
                  {notifications.length > 0 && (
                    <Typography 
                      fontSize="0.8rem" 
                      color={theme.palette.primary.main} 
                      sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                      onClick={handleClearNotifications}
                    >
                      Clear All
                    </Typography>
                  )}
                </FlexBetween>
                {notifications.length === 0 ? (
                  <Typography color={dark}>No notifications yet.</Typography>
                ) : (
                  [...notifications].reverse().map((notif, i) => (
                    <Box key={i} mb="0.5rem" pb="0.5rem" borderBottom={i === notifications.length - 1 ? "none" : `1px solid ${neutralLight}`}>
                      <Typography color={dark} fontSize="0.85rem">{notif}</Typography>
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Box>
          <FormControl variant="standard" value={fullName}>
            <Select
              value={fullName}
              sx={{
                backgroundColor: neutralLight,
                width: "150px",
                borderRadius: "0.25rem",
                p: "0.25rem 1rem",
                "& .MuiSvgIcon-root": {
                  pr: "0.25rem",
                  width: "3rem",
                },
                "& .MuiSelect-select:focus": {
                  backgroundColor: neutralLight,
                },
              }}
              input={<InputBase />}
            >
              <MenuItem value={fullName}>
                <Typography>{fullName}</Typography>
              </MenuItem>
              <MenuItem onClick={() => dispatch(setLogout())}>Log Out</MenuItem>
            </Select>
          </FormControl>
        </FlexBetween>
      ) : (
        <IconButton
          onClick={() => setIsMobileMenuToggled(!isMobileMenuToggled)}
        >
          <Menu />
        </IconButton>
      )}

      {/* MOBILE NAV */}
      {!isNonMobileScreens && isMobileMenuToggled && (
        <Box
          position="fixed"
          right="0"
          bottom="0"
          height="100%"
          zIndex="10"
          maxWidth="500px"
          minWidth="300px"
          backgroundColor={background}
        >
          {/* CLOSE ICON */}
          <Box display="flex" justifyContent="flex-end" p="1rem">
            <IconButton
              onClick={() => setIsMobileMenuToggled(!isMobileMenuToggled)}
            >
              <Close />
            </IconButton>
          </Box>

          {/* MENU ITEMS */}
          <FlexBetween
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="3rem"
          >
            <IconButton
              onClick={() => dispatch(setMode())}
              sx={{ fontSize: "25px" }}
            >
              {theme.palette.mode === "dark" ? (
                <DarkMode sx={{ fontSize: "25px" }} />
              ) : (
                <LightMode sx={{ color: dark, fontSize: "25px" }} />
              )}
            </IconButton>
            <IconButton onClick={() => setIsChatOpen(!isChatOpen)}>
              <Message sx={{ fontSize: "25px", color: dark }} />
            </IconButton>
            <Notifications sx={{ fontSize: "25px" }} />
            <FormControl variant="standard" value={fullName}>
              <Select
                value={fullName}
                sx={{
                  backgroundColor: neutralLight,
                  width: "150px",
                  borderRadius: "0.25rem",
                  p: "0.25rem 1rem",
                  "& .MuiSvgIcon-root": {
                    pr: "0.25rem",
                    width: "3rem",
                  },
                  "& .MuiSelect-select:focus": {
                    backgroundColor: neutralLight,
                  },
                }}
                input={<InputBase />}
              >
                <MenuItem value={fullName}>
                  <Typography>{fullName}</Typography>
                </MenuItem>
                <MenuItem onClick={() => dispatch(setLogout())}>
                  Log Out
                </MenuItem>
              </Select>
            </FormControl>
          </FlexBetween>
        </Box>
      )}

      {isChatOpen && <ChatBox onClose={() => setIsChatOpen(false)} />}
    </FlexBetween>
  );
};

export default Navbar;
